import { Card, CardContent } from '../../components/ui/card';
import { CalendarClock, Play, AlertTriangle, ListChecks, type LucideIcon } from 'lucide-react';
import type { AuditSummary } from '../../lib/api/audits';

interface Tile {
  label: string;
  value: string;
  sub?: string;
  icon: LucideIcon;
  iconClass: string;
  cardClass?: string;
}

/** Planned / In progress / Overdue / Open actions for everything in scope. */
export default function AuditKpiTiles({ summary, loading }: { summary: AuditSummary | null; loading: boolean }) {
  const tiles: Tile[] = [
    {
      label: 'Planned',
      value: summary ? summary.planned.toLocaleString() : '—',
      sub: summary ? `${summary.completed.toLocaleString()} completed` : undefined,
      icon: CalendarClock, iconClass: 'text-blue-500',
    },
    {
      label: 'In progress',
      value: summary ? summary.in_progress.toLocaleString() : '—',
      icon: Play, iconClass: 'text-yellow-500',
    },
    {
      label: 'Audits overdue',
      value: summary ? summary.overdue.toLocaleString() : '—',
      sub: 'Past the scheduled date, not completed',
      icon: AlertTriangle, iconClass: 'text-red-500',
      cardClass: summary && summary.overdue > 0 ? 'border-red-200 bg-red-50 dark:border-red-900 dark:bg-red-950/30' : undefined,
    },
    {
      label: 'Open actions',
      value: summary ? summary.open_actions.toLocaleString() : '—',
      sub: summary ? `${summary.overdue_actions.toLocaleString()} overdue` : undefined,
      icon: ListChecks, iconClass: 'text-purple-500',
    },
  ];
  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-4" data-testid="audit-kpi-tiles">
      {tiles.map((tile) => {
        const Icon = tile.icon;
        return (
          <Card key={tile.label} className={tile.cardClass}>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">{tile.label}</p>
                  {loading ? (
                    <div className="mt-1 h-8 w-16 animate-pulse rounded bg-muted" />
                  ) : (
                    <p className="text-2xl font-bold">{tile.value}</p>
                  )}
                  {tile.sub && !loading && <p className="text-xs text-muted-foreground">{tile.sub}</p>}
                </div>
                <Icon className={`h-8 w-8 ${tile.iconClass}`} />
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
